import { useApi } from "@core/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faLocationDot, faChevronDown, faSpinner } from "@fortawesome/pro-light-svg-icons";
import { minutes } from "@core/helpers";
import type { AccountContextItemDto } from "@/apidtos";
import { clearSiteTopBannerHeight, setSiteTopBannerHeight } from "@site/components/siteTopBanners";

export const AccountLocationBanner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey: ["accountContext"],
    queryFn: () => api.GetAccountContext(),
    staleTime: minutes(1),
  });

  const switchAccount = useMutation({
    mutationFn: (customerId: number) => api.SwitchActiveAccount(customerId),
    onSuccess: () => {
      queryClient.clear();
      location.reload();
    },
  });

  const canSwitch = !!data?.CanSwitch;

  // Sit below the emulation banner when both are present; publish our height into the shared top offset.
  useEffect(() => {
    if (!canSwitch) {
      clearSiteTopBannerHeight("account");
      return;
    }
    const update = () => {
      setSiteTopBannerHeight("account", bannerRef.current?.offsetHeight ?? 0);
    };
    update();
    const observer = new ResizeObserver(update);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      clearSiteTopBannerHeight("account");
    };
  }, [canSwitch]);

  if (!canSwitch || !data) return null;

  const active = data.Accounts.find((a) => a.IsActive);
  const isLocation = (t?: string) => (t ?? "").toLowerCase() === "location" || (t ?? "").toLowerCase() === "customer";

  return (
    <div
      ref={bannerRef}
      className="fixed left-0 right-0 z-[260] flex items-center justify-center gap-3 bg-[#0F2942] px-4 py-2 text-white print:hidden"
      style={{ top: "var(--site-top-banner-emu-height, 0px)" }}
    >
      <FontAwesomeIcon icon={active && isLocation(active.Type) ? faLocationDot : faBuilding} className="text-[#D4AF37]" />
      <span className="text-sm">
        Viewing as{" "}
        <span className="font-semibold">{active?.Name ?? "—"}</span>
        {active?.IsHome && <span className="text-gray-400"> (your account)</span>}
      </span>

      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={switchAccount.isPending}
          className="flex items-center gap-2 rounded border border-[#1a3a52] bg-[#13314c] px-3 py-1 text-xs font-medium hover:bg-[#1a3a52]"
        >
          {switchAccount.isPending ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              Switch account
              <FontAwesomeIcon icon={faChevronDown} className="w-3" />
            </>
          )}
        </button>

        {open && !switchAccount.isPending && (
          <div className="absolute right-0 top-full z-[261] mt-1 w-64 overflow-hidden rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg">
            {data.Accounts.map((a: AccountContextItemDto) => (
              <button
                key={a.CustomerId}
                onClick={() => {
                  setOpen(false);
                  if (!a.IsActive) switchAccount.mutate(a.CustomerId);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  a.IsActive ? "bg-gray-50 font-semibold" : ""
                }`}
              >
                <FontAwesomeIcon
                  icon={isLocation(a.Type) ? faLocationDot : faBuilding}
                  className="w-4 text-gray-400"
                />
                <span className="flex-1 truncate">{a.Name}</span>
                {a.IsHome && <span className="text-xs text-gray-400">Home</span>}
                {a.IsActive && <span className="text-xs text-[#137a3a]">Active</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
